import { useTranslation } from 'react-i18next'

import { usePassProducts } from '../../../../../hooks/passes/usePassProducts'

import Icon from '../../../../ui/Icon'
import { DropdownMenu, DropdownMenuItem } from '../../../../dropdownMenu/DropdownMenu'
import { PassManagerVersions } from './passManagerVersions/PassManagerVersions'
import { PassIntervals, PassStatusBadge, usePassFormat } from './passManagerParts'
import {
  HistoryToggle,
  ScheduleCourts,
  ScheduleDays,
  ScheduleTimeline,
  useVersionHistory,
} from '../../scheduleCard/scheduleParts'

import { PassColor, PassProduct } from '../../../../../types/booking/booking'

export enum PassAction {
  EDIT = 'EDIT',
  ACTIVATE = 'ACTIVATE',
  DEACTIVATE = 'DEACTIVATE',
  SUSPEND = 'SUSPEND',
  UNSUSPEND = 'UNSUSPEND',
}

type PassManagerCardProps = {
  product: PassProduct
  courtNames: Map<number | undefined, string>
  /** The pass's club; its opening hours are the span of the timeline. */
  clubName?: string
  openTime?: string
  closeTime?: string
  onAction: (product: PassProduct, action: PassAction) => void
}

/**
 * One pass product (its current version). Older versions are fetched for this pass only, the first time the history
 * is expanded.
 */
export const PassManagerCard = ({ product, courtNames, clubName, openTime, closeTime, onAction }: PassManagerCardProps) => {
  const { t } = useTranslation('passes')
  const { versions } = usePassProducts()
  const format = usePassFormat()
  const history = useVersionHistory(() => versions(product.id!))

  const hasHistory = (product.version ?? 1) > 1
  const menu: DropdownMenuItem[] = [
    { key: 'edit', label: t('passes.manager.edit'), icon: 'edit', onClick: () => onAction(product, PassAction.EDIT) },
    product.active
      ? { key: 'deactivate', label: t('passes.manager.deactivate'), icon: 'eye', onClick: () => onAction(product, PassAction.DEACTIVATE) }
      : { key: 'activate', label: t('passes.manager.activate'), icon: 'eye', onClick: () => onAction(product, PassAction.ACTIVATE) },
    product.suspended
      ? { key: 'unsuspend', label: t('passes.manager.unsuspend'), icon: 'check', onClick: () => onAction(product, PassAction.UNSUSPEND) }
      : { key: 'suspend', label: t('passes.manager.suspend'), icon: 'block', danger: true, onClick: () => onAction(product, PassAction.SUSPEND) },
  ]

  return (
    <div className="ph-card schedule-card pass-manager-card">
      {/* The stripe shows the pass's card colour. */}
      <span
        className={`schedule-card-stripe pass-stripe-${(product.color ?? PassColor.DEFAULT).toLowerCase()}`}
        aria-hidden="true"
      />

      <div className="schedule-card-content">
        <div className="schedule-card-header">
          <div className="schedule-card-title">
            <h3>{product.name}</h3>
            {product.mostPopular && (
              <span className="badge badge-accent pass-manager-card-popular">
                <Icon name="star" size={11} />
                {t('passes.form.fields.most-popular')}
              </span>
            )}
            {clubName && <span className="schedule-card-meta">{clubName}</span>}
            <span className="schedule-card-meta mono">{t('passes.manager.card.version', { version: product.version })}</span>
            <span className="schedule-card-meta">{format.since(product)}</span>
            {hasHistory && (
              <HistoryToggle
                open={history.open}
                label={t(history.open ? 'passes.manager.card.hide-history' : 'passes.manager.card.show-history')}
                onToggle={history.toggle}
              />
            )}
          </div>
          <div className="schedule-card-corner">
            <PassStatusBadge product={product} />
            <DropdownMenu label={t('passes.manager.card.more')} items={menu} />
          </div>
        </div>

        <div className="schedule-card-facts">
          <div className="schedule-card-fact">
            <span className="schedule-card-label">
              <Icon name="tag" size={14} />
              {t('passes.manager.card.price')}
            </span>
            <span className="mono schedule-card-value">{format.price(product)}</span>
          </div>
          <div className="schedule-card-fact">
            <span className="schedule-card-label">
              <Icon name="ball" size={14} />
              {t('passes.manager.card.minutes')}
            </span>
            <span className="mono schedule-card-value">{format.minutes(product)}</span>
          </div>
          <div className="schedule-card-fact">
            <span className="schedule-card-label">
              <Icon name="flag" size={14} />
              {t('passes.manager.card.validity')}
            </span>
            <span className="mono schedule-card-value">{format.validity(product)}</span>
          </div>
          <div className="schedule-card-fact">
            <span className="schedule-card-label">
              <Icon name="calendar" size={14} />
              {t('passes.manager.card.book-ahead')}
            </span>
            <span className="mono schedule-card-value">{format.bookAhead(product)}</span>
          </div>
          <div className="schedule-card-fact is-hours">
            <span className="schedule-card-label">
              <Icon name="clock" size={14} />
              {t('passes.manager.card.hours')}
            </span>
            <PassIntervals intervals={product.intervals} />
          </div>
          <div className="schedule-card-fact is-days">
            <span className="schedule-card-label">
              <Icon name="calendar" size={14} />
              {t('passes.manager.card.days')}
            </span>
            <ScheduleDays weekdays={product.weekdays} />
          </div>
          <div className="schedule-card-fact is-courts">
            <span className="schedule-card-label">
              <Icon name="court" size={14} />
              {t('passes.manager.card.courts')}
            </span>
            <ScheduleCourts courtIds={product.courtIds} courtNames={courtNames} />
          </div>
        </div>

        <ScheduleTimeline intervals={product.intervals} openTime={openTime} closeTime={closeTime} />

        {history.open && <PassManagerVersions history={history.history} courtNames={courtNames} />}
      </div>
    </div>
  )
}
